import "../styles/modes.css";
import { LevelCatalog, Modes } from "@highmarch/app";
import { BalanceData, TowerArchetype, ironHpMult } from "@highmarch/domain";
import { btnHtml, fmtCompact } from "../kit";
import { icon } from "../iconsKit";
import { sheetHeadHtml, wellHtml } from "../components";
import { evBannerHtml, evChipHtml, evKickHtml, factChipHtml } from "../hub/evKit";
import { SHOP_SKIN } from "../shopSkin";
import { portraitImg, portraitUrl } from "../portraits";
import { marksGlyph } from "../hub/eventRewards";
import { fmtLeft } from "../hub/eventsSheet";
import { ironStarGlyph } from "../modeGlyphs";
import { paintedIconHtml } from "../featureIcons";
import { bindMilestoneClaims, milestoneTrackHtml } from "../forge/milestoneTrack";

/** What the Iron sheet needs: progress, the modes use cases, the clock and a way to start the run. */
export interface IronSheetCtx {
  readonly catalog: LevelCatalog;
  readonly modes: Modes;
  readonly now: () => number;
  /** Starts the Iron run (the sheet goes first). */
  readonly start: (level: number) => void;
  /** Saves and redraws the hub after a milestone claim (gear v2's Iron star totals). */
  readonly onChange?: () => void;
}

const SECONDS_PER_DAY = 86_400;
/** The sheet head's painted star shield (CSS px), as Daily Road's. */
const HEAD_ICON_PX = 52;
/** Tower portrait height on the sheet's three cards (CSS px). */
const TOWER_ART_PX = 64;
/** The boss face beside the wave's facts. */
const BOSS_ART_PX = 40;

/** A tower's job in a few words, from its data: what the three Iron towers each bring to the one long wave. */
export function towerRole(t: TowerArchetype): string {
  const parts: string[] = [];
  if (t.splashRadius.isPositive()) parts.push("splash");
  if (t.chainTargets > 0) parts.push("chains");
  if (t.slowFraction.isPositive()) parts.push("slows");
  if (t.burnDamagePerSecond.isPositive()) parts.push("burns");
  if (t.damage.isZero()) parts.push("support");
  if (parts.length === 0) parts.push("single target");
  return `${parts.join(" · ")}${t.targetsAir ? " · hits flyers" : t.targetsGround ? " · ground only" : ""}`;
}

/** "×1.2 health", "−20 % health": how the Iron wave's raiders compare with the level's. */
export function ironHealthLine(B: BalanceData, level: number): string {
  const m = ironHpMult(B, level).toNumber();
  if (Math.abs(m - 1) < 0.005) return "level health";
  return m > 1 ? `+${Math.round((m - 1) * 100)}% health` : `${Math.round((m - 1) * 100)}% health`;
}

function bodyHtml(ctx: IronSheetCtx, level: number): string {
  const B = ctx.catalog.balance, m = ctx.modes, now = ctx.now();
  const L = B.level(level), stages = B.stagesOf(level), boss = B.enemies[L.bossEnemyIndex];
  const towers = m.ironTowers(level).map((i) => B.towers[i]);
  const tried = ctx.catalog.modes.triedToday(level, now), star = ctx.catalog.modes.hasIronStar(level);
  const resetIn = fmtLeft(SECONDS_PER_DAY - (now % SECONDS_PER_DAY));
  const marks = m.ironMarks(level);
  const cards = towers.map((t) => `<div class="irtower"><div class="art">${portraitImg(t.id, TOWER_ART_PX, icon("tower", 36), "", 3)}</div><b class="disp">${t.name}</b><small>${towerRole(t)}</small></div>`).join("");
  const bossArt = portraitUrl(boss.id);
  const chips = `${evChipHtml(`Level ${level}`)}${evChipHtml(`${icon("clock", 13)}${tried ? resetIn : "1 try a day"}`)}`;
  const part = (art: string, name: string, count = "", dim = false): string => `<span class="evpart">${wellHtml({ art, count, dim, tag: "span", rarity: name === "Iron star" ? 4 : null })}<i class="evpn">${name}</i></span>`;
  const reward = `<div class="evparts">${star ? "" : part(ironStarGlyph(34), "Iron star")}${part(marksGlyph(34), "Marks", fmtCompact(marks), tried)}</div>`;
  const cta = btnHtml(tried ? (star ? "WON" : "TRIED") : "START", tried ? "dark" : "gold", { icon: tried ? (star ? "star" : "lock") : "play", className: "disp go irongo", disabled: tried });
  return `
    ${evBannerHtml({ scene: "eviron", title: "One long wave", chips })}
    <div class="evfacts">${factChipHtml(icon("sword", 13), `${stages} waves in one`)}${factChipHtml(icon("heart", 13), ironHealthLine(B, level))}
      ${factChipHtml(bossArt ? `<img decoding="sync" src="${bossArt}" alt="" width="${BOSS_ART_PX / 2}" height="${BOSS_ART_PX / 2}">` : icon("skull", 13), boss.name)}</div>
    <div class="irplay"><div class="irreward">${reward}</div>${cta}</div>
    ${evKickHtml("YOUR THREE TOWERS")}
    <div class="irtowers">${cards}</div>
    ${ironMilestonesHtml(ctx)}`;
}

/** Gear v2's free route: the Iron star totals (one-off milestones over every level's Iron star) with CLAIM. */
export function ironMilestonesHtml(ctx: Pick<IronSheetCtx, "catalog" | "modes">): string {
  const rows = ctx.modes.ironMilestones();
  if (rows.length === 0) return "";
  const stars = ctx.catalog.modes.ironStarCount, ready = rows.filter((r) => r.reached && !r.claimed).length;
  return `${evKickHtml("IRON STAR MILESTONES", ready > 0 ? `${ready} to claim` : `${stars} so far`)}
    ${milestoneTrackHtml(rows, "iron", -1)}`;
}

/**
 * A level's Iron challenge (docs/MODES.md), before the start: the one long wave, the three towers the draft will deal
 * (chosen per level from its enemy mix), the reward and today's try. START spends the try as the run begins.
 */
export function showIronSheet(parent: HTMLElement, ctx: IronSheetCtx, level: number): HTMLElement {
  parent.querySelector(":scope > .sheet.iron")?.remove();
  const sheet = document.createElement("div");
  sheet.className = `sheet panel iron ${SHOP_SKIN}`;
  const L = ctx.catalog.balance.level(level);
  const render = (): void => {
    const keep = sheet.querySelector<HTMLElement>(".irbody")?.scrollTop ?? 0;
    sheet.innerHTML = `${sheetHeadHtml({ title: "Iron Challenge", sub: `Level ${level} · ${L.name}`, portrait: paintedIconHtml("iron", HEAD_ICON_PX), tint: "navy" })}<div class="irbody">${bodyHtml(ctx, level)}</div>`;
    const body = sheet.querySelector<HTMLElement>(".irbody");
    if (body) body.scrollTop = keep;
    sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
    sheet.querySelector<HTMLButtonElement>(".irongo")?.addEventListener("click", () => {
      if (!ctx.modes.ironReady(level, ctx.now())) return;
      sheet.remove();
      ctx.start(level);
    });
    bindMilestoneClaims(sheet, { balance: ctx.catalog.balance, layerHost: sheet, toastHost: parent },
      (_k, _w, index) => { const c = ctx.modes.claimIronMilestone(index, ctx.now()); if (typeof c !== "string") ctx.onChange?.(); return c; },
      () => { if (sheet.isConnected) render(); });
  };
  parent.append(sheet);
  render();
  return sheet;
}
